import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { getCurrentUser, updateMe } from '@/lib/api/auth'
import { getUser, isAuthenticated, setAuthSession } from '@/lib/auth/session'
import type { PlatformUser } from '@/types'
import { Button, Input, Label } from '@rr/ui'

export default function ProfilePage() {
  const navigate = useNavigate()
  const [user, setUser] = useState<PlatformUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    // Check authentication
    if (!isAuthenticated()) {
      navigate('/login')
      return
    }

    // Get user data
    const userData = getUser()
    if (userData) {
      setUser(userData)
      setFormData({
        name: (userData as any).fullName || (userData as any).name || '',
        email: userData.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    }

    setLoading(false)
  }, [navigate])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    // Password validation only if trying to change password
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required'
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters'
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirm password is required'
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match'
      }
    }

    setFormErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    setErrorMessage('')
    setSuccessMessage('')

    if (!validateForm()) {
      return
    }

    setSaving(true)

    try {
      const updateData: any = {
        name: formData.name,
        email: formData.email,
      }

      if (formData.newPassword) {
        updateData.password = formData.newPassword
      }

      const result = await updateMe(updateData)

      // Update session with new user data
      const accessToken = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken')
      const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken')

      if (accessToken && refreshToken) {
        setAuthSession(accessToken, refreshToken, result, true)
      }

      setUser(result)
      setEditing(false)
      setSuccessMessage('Profile updated successfully')

      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: (user as any).fullName || (user as any).name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
      setFormErrors({})
    }
    setEditing(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-info border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-subtext">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-h1 font-semibold mb-2">My Profile</h1>
          <p className="text-subtext">Manage your account settings and personal information</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4">
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 bg-danger/10 border border-danger/20 rounded-lg p-4">
            <p className="text-sm text-danger">{errorMessage}</p>
          </div>
        )}

        <div className="bg-card rounded-lg border border-border p-8">
          {!editing ? (
            <>
              {/* View Mode */}
              <div className="space-y-6 mb-6">
                <div>
                  <p className="text-sm text-subtext mb-1">Name</p>
                  <p className="text-text font-medium">{(user as any).fullName || (user as any).name || 'N/A'}</p>
                </div>

                <div>
                  <p className="text-sm text-subtext mb-1">Email</p>
                  <p className="text-text font-medium">{user.email}</p>
                </div>

                <div>
                  <p className="text-sm text-subtext mb-1">User Type</p>
                  <p className="text-text font-medium capitalize">{(user as any).userType || 'platform'}</p>
                </div>

                <div>
                  <p className="text-sm text-subtext mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <p className="text-text font-medium capitalize">{(user as any).status || 'Active'}</p>
                  </div>
                </div>
              </div>

              {/* Edit Button */}
              <div className="flex gap-3 pt-6 border-t border-border">
                <Button onClick={() => setEditing(true)}>
                  Edit Profile
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Edit Mode */}
              <div className="space-y-5">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    disabled={saving}
                    className={formErrors.name ? 'border-red-500' : ''}
                  />
                  {formErrors.name && <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your email"
                    disabled={saving}
                    className={formErrors.email ? 'border-red-500' : ''}
                  />
                  {formErrors.email && <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>}
                </div>

                <div className="pt-4 border-t border-border">
                  <h3 className="text-sm font-semibold text-text mb-4">Change Password (Optional)</h3>

                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      placeholder="Leave blank to keep current password"
                      disabled={saving}
                      className={formErrors.newPassword ? 'border-red-500' : ''}
                    />
                    {formErrors.newPassword && <p className="text-sm text-red-500 mt-1">{formErrors.newPassword}</p>}
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      disabled={saving}
                      className={formErrors.confirmPassword ? 'border-red-500' : ''}
                    />
                    {formErrors.confirmPassword && <p className="text-sm text-red-500 mt-1">{formErrors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-6 border-t border-border mt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
